import React, { useEffect, useMemo, useState } from 'react';
import { View, Text, TextInput, TouchableOpacity, ScrollView, StyleSheet } from 'react-native';
import { getAllNotes, createNote, updateNote, deleteNote, deleteAllNotes } from '../../utils/notesStorage';
import { useAppTheme } from '../../utils/useAppTheme';

const NotesPage = () => {
  const { colors } = useAppTheme();
  const styles = useMemo(() => createStyles(colors), [colors]);
  const [notes, setNotes] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [editorVisible, setEditorVisible] = useState(false);
  const [editingNote, setEditingNote] = useState(null);
  const [editorTitle, setEditorTitle] = useState('');
  const [editorContent, setEditorContent] = useState('');

  useEffect(() => {
    loadNotes();
  }, []);

  const loadNotes = async () => {
    const data = await getAllNotes();
    setNotes(data);
  };

  const handleNewNote = () => {
    setEditingNote(null);
    setEditorTitle('');
    setEditorContent('');
    setEditorVisible(true);
  };

  const handleEditNote = (note) => {
    setEditingNote(note);
    setEditorTitle(note.title);
    setEditorContent(note.content);
    setEditorVisible(true);
  };

  const handleSaveNote = async () => {
    const title = editorTitle.trim();
    const content = editorContent.trim();

    if (!title && !content) {
      setEditorVisible(false);
      return;
    }

    if (editingNote) {
      await updateNote(editingNote.id, { title: title || 'Untitled Note', content });
    } else {
      await createNote(title, content);
    }
    setEditorVisible(false);
    loadNotes();
  };

  const handleDeleteNote = async (id) => {
    if (window.confirm('Delete this note?')) {
      await deleteNote(id);
      loadNotes();
    }
  };

  const handleDeleteAll = async () => {
    if (notes.length === 0) return;
    if (window.confirm('Delete ALL notes? This cannot be undone!')) {
      await deleteAllNotes();
      loadNotes();
    }
  };

  const formatDate = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffDays = Math.floor(diffMs / 86400000);
    if (diffDays < 1) return 'Today';
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  const filteredNotes = searchQuery.trim()
    ? notes.filter(n =>
        n.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        n.content.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : notes;

  if (editorVisible) {
    return (
      <View style={styles.container}>
        <View style={styles.editorHeader}>
          <TouchableOpacity onPress={() => setEditorVisible(false)} style={styles.editorBtn}>
            <Text style={styles.cancelText}>← Back</Text>
          </TouchableOpacity>
          <Text style={styles.editorTitle}>{editingNote ? 'Edit Note' : 'New Note'}</Text>
          <TouchableOpacity onPress={handleSaveNote} style={styles.saveBtn}>
            <Text style={styles.saveText}>Save</Text>
          </TouchableOpacity>
        </View>
        <TextInput
          style={styles.titleInput}
          placeholder="Title"
          placeholderTextColor={colors.textSecondary}
          value={editorTitle}
          onChangeText={setEditorTitle}
          autoCapitalize="sentences"
        />
        <TextInput
          style={styles.contentInput}
          placeholder="Start writing..."
          placeholderTextColor={colors.textSecondary}
          value={editorContent}
          onChangeText={setEditorContent}
          multiline
          textAlignVertical="top"
        />
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <View style={styles.header}>
        <Text style={styles.pageTitle}>📝 Notes</Text>
        <View style={styles.headerActions}>
          {notes.length > 0 && (
            <TouchableOpacity onPress={handleDeleteAll} style={styles.deleteAllBtn}>
              <Text style={styles.deleteAllText}>Delete All</Text>
            </TouchableOpacity>
          )}
          <TouchableOpacity onPress={handleNewNote} style={styles.newBtn}>
            <Text style={styles.newBtnText}>+ New Note</Text>
          </TouchableOpacity>
        </View>
      </View>

      {notes.length > 0 && (
        <TextInput
          style={styles.searchInput}
          placeholder="Search notes..."
          placeholderTextColor={colors.textSecondary}
          value={searchQuery}
          onChangeText={setSearchQuery}
        />
      )}

      <ScrollView style={styles.notesList}>
        {filteredNotes.length === 0 ? (
          <View style={styles.emptyState}>
            <Text style={styles.emptyIcon}>📝</Text>
            <Text style={styles.emptyText}>
              {searchQuery ? 'No matching notes' : 'No notes yet'}
            </Text>
            {!searchQuery && (
              <Text style={styles.emptySubtext}>Click "+ New Note" to create your first note</Text>
            )}
          </View>
        ) : (
          filteredNotes.map((note) => (
            <TouchableOpacity
              key={note.id}
              style={styles.noteCard}
              onPress={() => handleEditNote(note)}
            >
              <View style={styles.noteContent}>
                <Text style={styles.noteTitle}>{note.title}</Text>
                {note.content ? (
                  <Text style={styles.notePreview} numberOfLines={2}>{note.content}</Text>
                ) : null}
                <Text style={styles.noteDate}>{formatDate(note.updatedAt)}</Text>
              </View>
              <TouchableOpacity
                style={styles.deleteBtn}
                onPress={() => handleDeleteNote(note.id)}
              >
                <Text style={styles.deleteBtnText}>🗑️</Text>
              </TouchableOpacity>
            </TouchableOpacity>
          ))
        )}
      </ScrollView>
    </View>
  );
};

const createStyles = (colors) => StyleSheet.create({
  container: {
    flex: 1,
    padding: 20,
    maxWidth: 700,
    alignSelf: 'center',
    width: '100%',
    backgroundColor: colors.background,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 16,
  },
  pageTitle: {
    fontSize: 24,
    fontWeight: 'bold',
    color: colors.text,
  },
  headerActions: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  deleteAllBtn: {
    paddingVertical: 8,
    paddingHorizontal: 12,
    borderRadius: 6,
    backgroundColor: colors.dangerSurface,
    borderWidth: 1,
    borderColor: colors.error,
  },
  deleteAllText: {
    fontSize: 13,
    color: colors.error,
    fontWeight: '500',
  },
  newBtn: {
    paddingVertical: 8,
    paddingHorizontal: 16,
    borderRadius: 6,
    backgroundColor: colors.primary,
  },
  newBtnText: {
    fontSize: 14,
    color: colors.onPrimary,
    fontWeight: '600',
  },
  searchInput: {
    backgroundColor: colors.inputBackground,
    color: colors.text,
    borderRadius: 8,
    padding: 12,
    fontSize: 14,
    marginBottom: 16,
    borderWidth: 1,
    borderColor: colors.border,
  },
  notesList: {
    flex: 1,
  },
  emptyState: {
    alignItems: 'center',
    paddingVertical: 60,
  },
  emptyIcon: {
    fontSize: 48,
    marginBottom: 12,
  },
  emptyText: {
    fontSize: 18,
    fontWeight: '600',
    color: colors.text,
    marginBottom: 8,
  },
  emptySubtext: {
    fontSize: 14,
    color: colors.textSecondary,
  },
  noteCard: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: colors.surface,
    padding: 16,
    borderRadius: 8,
    marginBottom: 10,
    borderWidth: 1,
    borderColor: colors.border,
  },
  noteContent: {
    flex: 1,
  },
  noteTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: colors.text,
    marginBottom: 4,
  },
  notePreview: {
    fontSize: 13,
    color: colors.textSecondary,
    lineHeight: 18,
    marginBottom: 4,
  },
  noteDate: {
    fontSize: 11,
    color: colors.textSecondary,
  },
  deleteBtn: {
    padding: 8,
    marginLeft: 12,
  },
  deleteBtnText: {
    fontSize: 16,
  },
  // Editor styles
  editorHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 20,
    paddingBottom: 12,
    borderBottomWidth: 1,
    borderBottomColor: colors.divider,
  },
  editorBtn: {
    padding: 8,
  },
  cancelText: {
    fontSize: 14,
    color: colors.primary,
    fontWeight: '500',
  },
  editorTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: colors.text,
  },
  saveBtn: {
    paddingVertical: 6,
    paddingHorizontal: 16,
    backgroundColor: colors.primary,
    borderRadius: 6,
  },
  saveText: {
    fontSize: 14,
    color: colors.onPrimary,
    fontWeight: '600',
  },
  titleInput: {
    fontSize: 22,
    fontWeight: '600',
    color: colors.text,
    backgroundColor: colors.inputBackground,
    paddingVertical: 12,
    paddingHorizontal: 12,
    borderWidth: 1,
    borderColor: colors.border,
    borderRadius: 6,
    marginBottom: 16,
  },
  contentInput: {
    fontSize: 16,
    color: colors.text,
    backgroundColor: colors.inputBackground,
    borderWidth: 1,
    borderColor: colors.border,
    borderRadius: 6,
    padding: 12,
    lineHeight: 24,
    minHeight: 300,
    textAlignVertical: 'top',
  },
});

export default NotesPage;
